import { Typography } from "@shared/ui.primitives";
import { cloudinaryLoader } from "@shared/utils.cloudinary";
import Image from "next/image";

import {
  imageContainer,
  labelContainer,
  tileContainer
} from "./AnniversaryGiftProductCard.css";

type Props = {
  gift: {
    id: string;
    label?: string | null;
    image: string;
    slug: string;
  };
  href: string;
  onClick?: () => void;
};

/**
 * AnniversaryGiftProductCard displays a clickable card for an anniversary gift.
 *
 * @param props - Component props.
 * @param props.gift - Gift details.
 * @param props.gift.id - Unique gift identifier.
 * @param props.gift.label - Gift label or name (optional).
 * @param props.gift.image - Gift image URL.
 * @param props.gift.slug - Gift slug for alt text.
 * @param props.href - URL to navigate to on click.
 * @param props.onClick - Optional click handler.
 *
 * @returns A product card with image and label, wrapped in a link.
 */

export const AnniversaryGiftProductCard: React.FC<Props> = ({
  gift: { slug, image, label },
  href,
  onClick
}) => {
  return (
    <a href={href} onClick={onClick}>
      <div
        className={tileContainer}
        data-testid="anniversary-gift-product-card"
        aria-label="Anniversary gift product card"
      >
        <Image
          className={imageContainer}
          src={image}
          alt={slug}
          quality={100}
          width={409}
          height={292}
          priority
          sizes="300px"
          loader={cloudinaryLoader}
        />
        <div className={labelContainer}>
          <Typography variant="label-sm">{label}</Typography>
        </div>
      </div>
    </a>
  );
};
